import { useEffect } from 'react';
import { useForm, FormProvider, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '~/components/system/form-field';
import { MultiSelect } from '~/components/multi-select';
import { ImageSlotField } from '~/components/image-slot-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { contributorKeys, useContributor, useContributorMutations, uploadContributorImage } from '~/hooks/use-contributors';
import { useContributorGroupsList } from '~/hooks/use-contributor-groups';
import { useCountriesList } from '~/hooks/use-countries';
import {
	contributorSchema,
	CONTRIBUTOR_IMAGE_SLOTS,
	EMPTY_CONTRIBUTOR,
	tokenToForm,
	type ContributorFormValues
} from '~/lib/schemas/contributor';

const NONE = '__none__';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contributorId: number | null;
}

/** Create / edit a contributor. Loads the full record in edit mode; image bytes upload out-of-band. */
export function ContributorEditorSheet({ open, onOpenChange, contributorId }: Props) {
	const { t } = useTranslation();
	const isEdit = contributorId != null && contributorId > 0;
	const qc = useQueryClient();
	const { data: loaded, isLoading } = useContributor(open ? contributorId : null);
	const { create, update } = useContributorMutations();
	const { data: groups } = useContributorGroupsList();
	const { data: countries } = useCountriesList();

	const form = useForm<ContributorFormValues>({
		resolver: zodResolver(contributorSchema) as Resolver<ContributorFormValues>,
		defaultValues: EMPTY_CONTRIBUTOR,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;

	useEffect(() => {
		if (!open) return;
		if (isEdit) {
			if (loaded) reset(tokenToForm(loaded));
		} else {
			reset(EMPTY_CONTRIBUTOR);
		}
	}, [open, isEdit, loaded, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			let savedId = values.id ?? null;
			if (isEdit) await update.mutateAsync({ form: values, original: loaded ?? null });
			else {
				const created = await create.mutateAsync(values);
				savedId = created?.id ?? null;
			}
			// Image bytes upload out-of-band against the saved id (server commits immediately for id > 0).
			if (savedId) {
				let uploaded = false;
				for (const slot of CONTRIBUTOR_IMAGE_SLOTS) {
					const file = values[slot.fileField];
					if (file instanceof File) {
						await uploadContributorImage(savedId, slot.key, file);
						uploaded = true;
					}
				}
				if (uploaded) await qc.invalidateQueries({ queryKey: contributorKeys.detail(savedId) });
			}
			await qc.invalidateQueries({ queryKey: contributorKeys.all });
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
		<div className="flex flex-col gap-3 border-b border-border pb-4">
			<h3 className="text-sm font-semibold text-foreground">{title}</h3>
			{children}
		</div>
	);

	const SwitchRow = ({ name, label }: { name: keyof ContributorFormValues; label: string }) => (
		<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
			<Label htmlFor={`con-${String(name)}`}>{label}</Label>
			<Switch
				id={`con-${String(name)}`}
				checked={Boolean(watch(name))}
				onCheckedChange={next => setValue(name, next as never, { shouldDirty: true })}
			/>
		</div>
	);

	const cid = watch('cid');
	const selectedGroups = watch('grouplst');

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
				<SheetHeader className="border-b border-border px-5 py-4">
					<SheetTitle>{isEdit ? t('contributors.list.editTitle') : t('contributors.list.addTitle')}</SheetTitle>
					<SheetDescription>{t('contributors.list.formSubtitle')}</SheetDescription>
				</SheetHeader>

				{isEdit && isLoading ? (
					<div className="flex flex-col gap-3 p-5">
						{Array.from({ length: 8 }).map((_, i) => (
							<Skeleton key={i} className="h-9 w-full rounded-md" />
						))}
					</div>
				) : (
					<FormProvider {...form}>
						<form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
							<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-5 py-4">
								<Section title={t('contributors.list.tabs.basic')}>
									<div className="grid grid-cols-2 gap-3">
										<FormField label={t('contributors.list.fields.name')} error={formState.errors.fn?.message}>
											<Input {...register('fn')} maxLength={50} autoFocus />
										</FormField>
										<FormField label={t('contributors.list.fields.surname')} error={formState.errors.ln?.message}>
											<Input {...register('ln')} maxLength={50} />
										</FormField>
										<FormField label={t('contributors.list.fields.country')}>
											<Select
												value={cid != null && cid > 0 ? String(cid) : NONE}
												onValueChange={val => setValue('cid', val === NONE ? null : Number(val), { shouldDirty: true })}>
												<SelectTrigger className="w-full" aria-label={t('contributors.list.fields.country')}>
													<SelectValue placeholder={t('common.none')} />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={NONE}>{t('common.none')}</SelectItem>
													{(countries ?? [])
														.filter(c => c.id != null)
														.map(c => (
															<SelectItem key={c.id} value={String(c.id)}>
																{c.nm}
															</SelectItem>
														))}
												</SelectContent>
											</Select>
										</FormField>
										<FormField label={t('contributors.list.fields.dob')}>
											<Input
												type="date"
												value={(watch('dob') ?? '').slice(0, 10)}
												onChange={e => setValue('dob', e.target.value || null, { shouldDirty: true })}
											/>
										</FormField>
										<FormField label={t('contributors.list.fields.sort')} error={formState.errors.si?.message}>
											<Input type="number" {...register('si', { setValueAs: v => (v === '' || v == null ? null : Number(v)) })} />
										</FormField>
										<FormField label={t('contributors.list.fields.erp')}>
											<Input {...register('erpcd')} maxLength={50} />
										</FormField>
									</div>
									<div className="grid grid-cols-2 gap-3">
										<SwitchRow name="iv" label={t('contributors.list.fields.visible')} />
										<SwitchRow name="frontExclusion" label={t('contributors.list.fields.frontExclusion')} />
									</div>
									<FormField label={t('contributors.list.fields.groups')}>
										<MultiSelect<number>
											value={selectedGroups}
											onChange={next => setValue('grouplst', next, { shouldDirty: true })}
											options={(groups ?? []).map(g => g.id!).filter(id => id != null)}
											getKey={id => String(id)}
											getLabel={id => (groups ?? []).find(g => g.id === id)?.nm ?? String(id)}
											placeholder={t('contributors.list.fields.groups')}
										/>
									</FormField>
								</Section>

								<Section title={t('contributors.list.tabs.seo')}>
									<div className="grid grid-cols-2 gap-3">
										<FormField label={t('contributors.list.fields.seoUrl')} error={formState.errors.eurl?.message}>
											<Input {...register('eurl')} maxLength={400} />
										</FormField>
										<FormField label={t('contributors.list.fields.landingUrl')} error={formState.errors.landingUrl?.message}>
											<Input {...register('landingUrl')} maxLength={400} />
										</FormField>
									</div>
								</Section>

								<Section title={t('contributors.list.tabs.images')}>
									{!isEdit && <p className="text-xs text-foreground-muted">{t('contributors.list.images.saveFirst')}</p>}
									<div className="grid grid-cols-2 gap-4">
										{CONTRIBUTOR_IMAGE_SLOTS.map(slot => (
											<ImageSlotField
												key={slot.key}
												urlField={slot.urlField}
												fileField={slot.fileField}
												delField={slot.delField}
												label={t('contributors.list.images.slot', { n: slot.key })}
												uploadLabel={t('contributors.list.images.upload')}
												replaceLabel={t('contributors.list.images.replace')}
											/>
										))}
									</div>
								</Section>
							</div>

							<SheetFooter className="border-t border-border px-5 py-4">
								<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
									{t('common.cancel')}
								</Button>
								<Button type="submit" disabled={create.isPending || update.isPending}>
									{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
								</Button>
							</SheetFooter>
						</form>
					</FormProvider>
				)}
			</SheetContent>
		</Sheet>
	);
}
